import type { Notes } from '@octopus-do/octopus-project-core'

import {
  RANK_MATH_KEYWORDS_LIMIT,
  RANK_MATH_META_KEYS,
  YOAST_META_KEYS,
} from './constants'
import type { WxrPageItem, WxrPostMetaItem } from './types'

const SLUG_FORBIDDEN_PATTERN = /[^\p{L}\p{N}\-_]+/gu

const getNote = (notes: Notes | null, key: keyof Notes): string => {
  const value = notes?.[key]

  return typeof value === 'string' ? value.trim() : ''
}

const getKeywords = (notes: Notes | null): string[] =>
  getNote(notes, 'keywords')
    .split(',')
    .map((keyword) => keyword.trim())
    .filter(Boolean)

export const sanitizeSlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(SLUG_FORBIDDEN_PATTERN, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')

// seoSlug чаще всего содержит путь (/blog/post/) или целый URL, а post_name — это
// один сегмент: иерархию в WXR задаёт wp:post_parent.
export const getSlugFromSeoSlug = (value: string): string => {
  const [path] = value.split(/[?#]/)
  const segments = path.split('/').filter((segment) => segment.trim() !== '')

  return sanitizeSlug(segments[segments.length - 1] || '')
}

export const getPageSlug = (
  page: Pick<WxrPageItem, 'notes' | 'title'>,
  withSeo: boolean
): string => {
  const seoSlug = withSeo
    ? getSlugFromSeoSlug(getNote(page.notes, 'seoSlug'))
    : ''

  return seoSlug || sanitizeSlug(page.title)
}

export const getSeoExcerpt = (notes: Notes | null, withSeo: boolean): string =>
  withSeo ? getNote(notes, 'seoDescription') : ''

export const getSeoPostMeta = (
  notes: Notes | null,
  withSeo: boolean
): WxrPostMetaItem[] => {
  if (!withSeo) {
    return []
  }

  const items: WxrPostMetaItem[] = []
  const title = getNote(notes, 'seoTitle')
  const description = getNote(notes, 'seoDescription')
  const keywords = getKeywords(notes)

  if (title) {
    items.push(
      { key: YOAST_META_KEYS.title, value: title },
      { key: RANK_MATH_META_KEYS.title, value: title }
    )
  }

  if (description) {
    items.push(
      { key: YOAST_META_KEYS.description, value: description },
      { key: RANK_MATH_META_KEYS.description, value: description }
    )
  }

  if (keywords.length) {
    items.push(
      { key: YOAST_META_KEYS.focusKeyword, value: keywords[0] },
      {
        key: RANK_MATH_META_KEYS.focusKeyword,
        value: keywords.slice(0, RANK_MATH_KEYWORDS_LIMIT).join(', '),
      }
    )
  }

  return items
}
